import { useEffect, useRef, useState } from "react";

const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [color, setColor] = useState("#00a878");
  const [isTouch, setIsTouch] = useState(false);
  const [touchActive, setTouchActive] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Detect touch device
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    setIsTouch(hasTouch);

    if (!hasTouch) {
      // ---------------- DESKTOP LOGIC ----------------
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvasRef.current = canvas;

      const moveHandler = (e: MouseEvent) => {
        setPosition({ x: e.clientX, y: e.clientY });

        const el = document.elementFromPoint(e.clientX, e.clientY);

        if (el instanceof HTMLImageElement) {
          canvas.width = el.naturalWidth;
          canvas.height = el.naturalHeight;
          ctx?.drawImage(el, 0, 0);

          const rect = el.getBoundingClientRect();
          const scaleX = el.naturalWidth / rect.width;
          const scaleY = el.naturalHeight / rect.height;

          const imgX = Math.floor((e.clientX - rect.left) * scaleX);
          const imgY = Math.floor((e.clientY - rect.top) * scaleY);

          const pixel = ctx?.getImageData(imgX, imgY, 1, 1).data;
          if (pixel) {
            const [r, g, b] = pixel;
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            setColor(brightness < 128 ? "#f5f5f5" : "#00a878");
          }
        } else {
          const bg = el ? window.getComputedStyle(el).backgroundColor : null;
          if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
            const match = bg.match(/rgba?\((\d+), (\d+), (\d+)/);
            if (match) {
              const r = parseInt(match[1], 10);
              const g = parseInt(match[2], 10);
              const b = parseInt(match[3], 10);
              const brightness = (r * 299 + g * 587 + b * 114) / 1000;
              setColor(brightness < 128 ? "#f5f5f5" : "#00a878");
            }
          } else {
            setColor("#00a878");
          }
        }
      };

      const addHoverEvents = () => {
        const elements = document.querySelectorAll("button, a, .cursor-hover");
        elements.forEach((el) => {
          el.addEventListener("mouseenter", () => setHovered(true));
          el.addEventListener("mouseleave", () => setHovered(false));
        });
      };

      window.addEventListener("mousemove", moveHandler);
      addHoverEvents();

      return () => {
        window.removeEventListener("mousemove", moveHandler);
      };
    } else {
      // ---------------- MOBILE LOGIC ----------------
      const touchStart = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        setPosition({ x: touch.clientX, y: touch.clientY });
        setTouchActive(true);
      };

      const touchMove = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        setPosition({ x: touch.clientX, y: touch.clientY });
      };

      const touchEnd = () => {
        setTouchActive(false);
      };

      window.addEventListener("touchstart", touchStart);
      window.addEventListener("touchmove", touchMove);
      window.addEventListener("touchend", touchEnd);

      return () => {
        window.removeEventListener("touchstart", touchStart);
        window.removeEventListener("touchmove", touchMove);
        window.removeEventListener("touchend", touchEnd);
      };
    }
  }, []);

  return (
    <>
      {/* Hide system cursor only on desktop */}
      {!isTouch && (
        <style>{`
          * {
            cursor: none !important;
          }
          @keyframes heartbeat {
            0%, 40%, 80%, 100% {
              transform: scale(1);
            }
            20%, 60% {
              transform: scale(1.2);
            }
          }
          .heartbeat {
            animation: heartbeat 0.8s infinite;
          }
        `}</style>
      )}

      {/* Desktop Cursor */}
      {!isTouch && (
        <div
          className="fixed top-0 left-0 z-[9999] pointer-events-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
          }}
        >
          <div
            className={`rounded-full transition-all duration-200 ease-out ${
              hovered ? "heartbeat" : ""
            }`}
            style={{
              width: hovered ? "16px" : "11px",
              height: hovered ? "16px" : "11px",
              marginLeft: "-50%",
              marginTop: "-50%",
              backgroundColor: color,
            }}
          />
        </div>
      )}

      {/* Mobile Touch Cursor */}
      {isTouch && touchActive && (
        <div
          className="fixed top-0 left-0 z-[9999] pointer-events-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
          }}
        >
          {/* Wrapper that centers dot inside ring */}
          <div
            className="relative flex items-center justify-center"
            style={{
              transform: "translate(-50%, -50%)", // center at finger
            }}
          >
            {/* Outer ring */}
            <div className="absolute w-10 h-10 rounded-full border-2 border-[#00a878] opacity-50 animate-ping" />

            {/* Inner dot */}
            <div className="w-6 h-6 rounded-full bg-[#00a878] opacity-50" />
          </div>
        </div>
      )}
    </>
  );
};

export default CustomCursor;
